/**
 * Utility to replace inline citations [1], [2, 3] etc with favicon badges
 */

export interface CitationSource {
    link?: string;
    url?: string;
    title?: string;
    source?: string;
}

/**
 * Get hostname from URL for favicon display
 */
export const getSafeHostname = (url: string): string => {
    if (!url || typeof url !== 'string') return '';
    try {
        const match = url.match(/^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:/\n?]+)/im);
        return match ? match[1] : url;
    } catch (e) {
        return url || '';
    }
};

/**
 * Replace citation brackets [1], [2, 3] with favicon elements
 * @param text - The text containing citations
 * @param sources - Array of sources (references or sources from Google AI response)
 * @returns HTML string with citations replaced by favicon badges
 */
export const replaceCitationsWithFavicons = (
    text: string,
    sources: CitationSource[]
): string => {
    if (!text || !sources || sources.length === 0) return text;

    // Pattern to match [1], [2], [1, 2], [3, 4, 5] etc.
    const citationPattern = /\[(\d+(?:\s*,\s*\d+)*)\]/g;

    return text.replace(citationPattern, (match, numbersStr) => {
        // Parse the numbers (e.g., "1, 2, 3" -> [1, 2, 3])
        const citationNumbers = numbersStr
            .split(',')
            .map((n: string) => parseInt(n.trim(), 10))
            .filter((n: number) => !isNaN(n));

        if (citationNumbers.length === 0) return match;

        // Generate favicon HTML for each citation
        const faviconElements = citationNumbers
            .map((num: number) => {
                const index = num - 1; // Convert 1-based to 0-based index
                const source = sources[index];

                if (!source) return null;

                const url = source.link || source.url || '';
                const hostname = getSafeHostname(url);
                const faviconUrl = `https://www.google.com/s2/favicons?sz=32&domain=${hostname}`;

                // Return inline HTML for favicon badge
                return `<span class="inline-citation-badge" title="${source.title || hostname}" data-url="${url}">
          <img 
            src="${faviconUrl}" 
            alt="${hostname}" 
            class="inline-citation-favicon"
            onerror="this.src='https://www.google.com/favicon.ico'"
          />
        </span>`;
            })
            .filter(Boolean)
            .join('');

        return faviconElements || match;
    });
};

/**
 * CSS styles for inline citation badges
 */
export const citationBadgeStyles = `
  .inline-citation-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 2px;
    padding: 2px;
    border-radius: 6px;
    background: rgba(59, 130, 246, 0.1);
    transition: all 0.2s ease;
    cursor: pointer;
    vertical-align: middle;
  }

  .inline-citation-badge:hover {
    background: rgba(59, 130, 246, 0.2);
    transform: scale(1.1);
  }

  .inline-citation-favicon {
    width: 14px;
    height: 14px;
    border-radius: 4px;
    object-fit: contain;
    display: block;
  }

  .dark .inline-citation-badge {
    background: rgba(96, 165, 250, 0.15);
  }

  .dark .inline-citation-badge:hover {
    background: rgba(96, 165, 250, 0.25);
  }
`;
